import React, { useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  TextField
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ImageUploader from './ImageUploader'
import axios from 'axios'

const FrameSelector: React.FC = () => {
  const [sourceImage, setSourceImage] = useState<string>('')
  const [sourceFile, setSourceFile] = useState<File | null>(null)
  const [frameStyle, setFrameStyle] = useState<string>('solid')
  const [frameWidth, setFrameWidth] = useState<number>(20)
  const [frameColor, setFrameColor] = useState<string>('#FFFFFF')
  const [borderRadius, setBorderRadius] = useState<number>(0)
  const [shadowEnabled, setShadowEnabled] = useState<boolean>(false)
  const [shadowBlur, setShadowBlur] = useState<number>(10)
  const [watermarkText, setWatermarkText] = useState<string>('')
  const [watermarkPosition, setWatermarkPosition] = useState<string>('bottom')
  const [watermarkOpacity, setWatermarkOpacity] = useState<number>(30)
  const [watermarkFontSize, setWatermarkFontSize] = useState<number>(24)
  const [framedImage, setFramedImage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const handleImageUpload = (imageData: string, file: File) => {
    setSourceImage(imageData)
    setSourceFile(file)
    setFramedImage('')
    setError('')
  }

  const handleStyleChange = (event: SelectChangeEvent) => {
    setFrameStyle(event.target.value)
  }

  const handleWidthChange = (_event: Event, newValue: number | number[]) => {
    setFrameWidth(newValue as number)
  }

  const handleRadiusChange = (_event: Event, newValue: number | number[]) => {
    setBorderRadius(newValue as number)
  }

  const handleBlurChange = (_event: Event, newValue: number | number[]) => {
    setShadowBlur(newValue as number)
  }

  const handleApplyFrame = async () => {
    if (!sourceImage) {
      setError('Proszę załadować obraz')
      return
    }

    setLoading(true)
    setError('')

    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        '/api/photo-tools/add-frame',
        {
          image: sourceImage,
          frame_style: frameStyle,
          frame_width: frameWidth,
          frame_color: frameColor,
          border_radius: borderRadius,
          shadow: shadowEnabled
            ? {
                enabled: true,
                blur: shadowBlur,
                color: '#00000080'
              }
            : null,
          watermark_text: watermarkText || null,
          watermark_position: watermarkPosition,
          watermark_opacity: watermarkOpacity,
          watermark_font_size: watermarkFontSize
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data.status === 'success') {
        setFramedImage(`data:image/png;base64,${response.data.framed_image}`)
      } else {
        setError(response.data.error || 'Nie udało się dodać ramki')
      }
    } catch (err: any) {
      console.error('Frame error:', err)
      setError(err.response?.data?.error || 'Wystąpił błąd podczas dodawania ramki')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (!framedImage) return

    const link = document.createElement('a')
    link.download = 'framed-image.png'
    link.href = framedImage
    link.click()
  }

  const handleReset = () => {
    setSourceImage('')
    setSourceFile(null)
    setFramedImage('')
    setError('')
    setFrameStyle('solid')
    setFrameWidth(20)
    setFrameColor('#FFFFFF')
    setBorderRadius(0)
    setShadowEnabled(false)
    setShadowBlur(10)
    setWatermarkText('')
    setWatermarkPosition('bottom')
    setWatermarkOpacity(30)
    setWatermarkFontSize(24)
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!sourceImage && (
        <ImageUploader
          label="Obraz do ramki"
          onImageUpload={handleImageUpload}
          currentImage={sourceImage}
        />
      )}

      {sourceImage && !framedImage && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Obraz źródłowy
              </Typography>
              <Box
                component="img"
                src={sourceImage}
                alt="Source"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '400px',
                  objectFit: 'contain'
                }}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Styl ramki</InputLabel>
                <Select
                  value={frameStyle}
                  label="Styl ramki"
                  onChange={handleStyleChange}
                >
                  <MenuItem value="solid">Jednolity kolor</MenuItem>
                  <MenuItem value="gradient">Gradient</MenuItem>
                </Select>
              </FormControl>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Szerokość ramki: {frameWidth}px
                </Typography>
                <Slider
                  value={frameWidth}
                  onChange={handleWidthChange}
                  min={1}
                  max={100}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Kolor ramki
                </Typography>
                <TextField
                  type="color"
                  value={frameColor}
                  onChange={(e) => setFrameColor(e.target.value)}
                  fullWidth
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Zaokrąglenie rogów: {borderRadius}px
                </Typography>
                <Slider
                  value={borderRadius}
                  onChange={handleRadiusChange}
                  min={0}
                  max={50}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Cień
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="checkbox"
                    checked={shadowEnabled}
                    onChange={(e) => setShadowEnabled(e.target.checked)}
                  />
                  <Typography variant="body2">Włącz cień</Typography>
                </Box>
                {shadowEnabled && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" gutterBottom>
                      Rozmycie: {shadowBlur}px
                    </Typography>
                    <Slider
                      value={shadowBlur}
                      onChange={handleBlurChange}
                      min={0}
                      max={50}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Watermark
                </Typography>
                <TextField
                  label="Tekst watermark (opcjonalnie)"
                  placeholder="np. yourmodel.eu"
                  value={watermarkText}
                  onChange={(e) => setWatermarkText(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  helperText="Pozostaw puste jeśli nie chcesz watermarku"
                />

                {watermarkText && (
                  <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Pozycja watermarku</InputLabel>
                      <Select
                        value={watermarkPosition}
                        label="Pozycja watermarku"
                        onChange={(e) => setWatermarkPosition(e.target.value)}
                      >
                        <MenuItem value="top">Góra</MenuItem>
                        <MenuItem value="bottom">Dół</MenuItem>
                      </Select>
                    </FormControl>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" gutterBottom>
                        Przezroczystość: {watermarkOpacity}%
                      </Typography>
                      <Slider
                        value={watermarkOpacity}
                        onChange={(_e, val) => setWatermarkOpacity(val as number)}
                        min={10}
                        max={100}
                        valueLabelDisplay="auto"
                      />
                    </Box>

                    <Box>
                      <Typography variant="caption" gutterBottom>
                        Rozmiar czcionki: {watermarkFontSize}px
                      </Typography>
                      <Slider
                        value={watermarkFontSize}
                        onChange={(_e, val) => setWatermarkFontSize(val as number)}
                        min={12}
                        max={72}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </>
                )}
              </Box>

              <Button
                variant="contained"
                onClick={handleApplyFrame}
                disabled={loading}
                fullWidth
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Dodaj ramkę'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                fullWidth
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      {framedImage && (
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Oryginał
                </Typography>
                <Box
                  component="img"
                  src={sourceImage}
                  alt="Original"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '400px',
                    objectFit: 'contain'
                  }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Z ramką
                </Typography>
                <Box
                  component="img"
                  src={framedImage}
                  alt="Framed"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    bgcolor: '#f5f5f5'
                  }}
                />
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Pobierz
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
            >
              Nowa ramka
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default FrameSelector
